/**
 * Extraction API
 *
 * Functions for document upload and triggering async extraction:
 * - uploadDocument: Upload a PO document to S3/MinIO and create queue entry
 * - extractDocument: Trigger extraction (resets to pending for agent pickup)
 *
 * Workflow:
 * 1. uploadDocument() -> creates queue entry with status "pending"
 * 2. extractDocument() -> resets to "pending" for Python agent pickup
 * 3. Frontend polls queueApi.getQueueEntry() until "extracted"
 * 4. User reviews and approves -> creates PurchaseOrder
 */

import api from './index';

/**
 * Upload a document for extraction
 *
 * Uploads a purchase order document to S3/MinIO and creates a queue entry.
 * Supports: PDF, PNG, JPEG, TIFF, Excel, CSV, EDI, TXT
 *
 * @param {File} file - The file object to upload (from input[type="file"])
 * @returns {Promise<Object>} Response with file metadata and queue entry
 */
export const uploadDocument = (file) => {
  const formData = new FormData();
  formData.append('file', file);

  return api.post('/extraction/upload', formData, {
    headers: { 'Content-Type': undefined }
  });
};

/**
 * Trigger extraction for a queued document
 *
 * Resets the queue entry to 'pending' so the Python agent watcher
 * picks it up via MongoDB change stream.
 *
 * @param {Object} params - Extraction parameters
 * @param {string} params.queueId - Queue entry ID
 * @returns {Promise<Object>} 202 response with { queueId, status: 'pending' }
 */
export const extractDocument = ({ queueId }) => {
  return api.post('/extraction/extract', { queueId });
};
